import type { Instance } from "mobx-state-tree";
import { types, flow } from "mobx-state-tree";
import type { PMIssueActivity, PMActivityActionType } from "@/domains/pm/types";
import * as pmRepo from "./repository";
import {
  timeRangeBounds,
  type NotificationsTimeRangeSelection,
} from "@/domains/pm/utils/notificationsTimeRange";

const FEED_PAGE_SIZE = 50;

/** Server-side filter selection for the activity feed (no offset/limit — the
 *  store owns pagination). Built by the PMNotifications page from its filter UI
 *  and passed to loadFeed; remembered so loadMoreFeed reuses it. */
export interface ActivityFeedQuery {
  buildingProjectId?: number;
  actionTypes?: PMActivityActionType[];
  actorIds?: number[];
  /** Resolved to `since`/`until` on every read, never once at selection —
   *  see `feedRepoOpts`. */
  timeRange?: NotificationsTimeRangeSelection;
  unreadOnly?: boolean;
  currentUserId?: number;
}

/** Repo options for one whole-feed read. Presets are relative to now, so they
 *  are resolved here, per read, rather than frozen into the remembered query.
 *  "Show more" reuses the options of the read that produced the rows on
 *  screen — its offset only means anything against that same window. */
function feedRepoOpts({ timeRange, ...rest }: ActivityFeedQuery) {
  return { ...rest, ...(timeRange ? timeRangeBounds(timeRange) : {}) };
}

/**
 * NotificationsStore (WHA-3733) — owns BOTH the nav red-dot unread count and
 * the Notifications page activity feed (rows, pagination, loading/error),
 * mirroring how IssuesStore owns the issue list. Scoped to the currently-viewed
 * building. BuildingLayout calls setContext + refresh on mount and on the
 * window 'franklin:pm-issue-changed' event; the page calls loadFeed/loadMoreFeed
 * and markSeen (which zeroes the count + dispatches 'franklin:pm-notifications-seen').
 *
 * "Unread" = activity newer than the user's last-seen high-water-mark
 * (pm_notifications_last_seen) authored by someone other than the user.
 */
export const NotificationsStore = types
  .model("NotificationsStore", {
    unreadCount: types.optional(types.number, 0),
  })
  .volatile(() => ({
    buildingId: 0 as number,
    currentUserId: 0 as number,
    projectId: null as number | null,
    // Activity feed state (WHA-3737: centralised in MST, not the component).
    feed: [] as PMIssueActivity[],
    feedTotal: 0 as number,
    isLoadingFeed: false as boolean,
    isLoadingMore: false as boolean,
    feedError: null as string | null,
    lastFeedBuildingId: 0 as number,
    lastFeedQuery: null as ActivityFeedQuery | null,
    // The building AND options of the rows on screen, kept as one pair so
    // "show more" can never page one building with another's filter.
    lastFeedPage: null as { buildingId: number; opts: ReturnType<typeof feedRepoOpts> } | null,
    // Monotonic token for feed reads. A realtime-driven refresh, a focus
    // revalidation and a filter change can all be in flight at once, and they
    // resolve in whatever order the network hands back — only the newest one
    // may write `feed`.
    feedRequestSeq: 0 as number,
  }))
  .views((self) => ({
    /** Whether more rows exist beyond what's loaded. */
    get hasMoreFeed() {
      return self.feed.length < self.feedTotal;
    },
  }))
  .actions((self) => ({
    setContext(buildingId: number, currentUserId: number, projectId?: number | null) {
      self.buildingId = buildingId;
      self.currentUserId = currentUserId;
      self.projectId = projectId ?? null;
    },
    refresh: flow(function* refresh() {
      if (!self.buildingId || !self.currentUserId) return;
      try {
        self.unreadCount = yield pmRepo.getUnreadActivityCountForBuilding(
          self.buildingId,
          self.currentUserId,
          self.projectId
        );
      } catch {
        // Best-effort: leave the dot at its previous value rather than break
        // the layout over a notification query.
      }
    }),
    markSeen: flow(function* markSeen() {
      if (!self.buildingId || !self.currentUserId) return;
      try {
        yield pmRepo.markNotificationsSeen(self.buildingId, self.currentUserId);
        self.unreadCount = 0;
      } catch {
        // ignore — non-fatal
      }
    }),
    // WHA-3737: load (or reload, on filter change) the first page of the feed.
    // This is the *visible* read — it shows the loading state and resets
    // pagination, so it belongs to a filter/building change, not to a
    // background event. Those go through refreshFeed.
    loadFeed: flow(function* loadFeed(buildingId: number, query: ActivityFeedQuery) {
      const seq = ++self.feedRequestSeq;
      if (self.lastFeedBuildingId !== buildingId) {
        self.feed = [];
        self.feedTotal = 0;
        self.lastFeedPage = null;
      }
      self.isLoadingMore = false;
      self.isLoadingFeed = true;
      self.feedError = null;
      self.lastFeedBuildingId = buildingId;
      self.lastFeedQuery = query;
      const opts = feedRepoOpts(query);
      try {
        const res: { data: PMIssueActivity[]; count: number } =
          yield pmRepo.getActivityFeedForBuilding(buildingId, {
            ...opts,
            offset: 0,
            limit: FEED_PAGE_SIZE,
          });
        if (seq !== self.feedRequestSeq) return;
        self.feed = res.data;
        self.feedTotal = res.count;
        // Only once these rows are the ones on screen: "show more" pages them.
        self.lastFeedPage = { buildingId, opts };
      } catch (e) {
        if (seq !== self.feedRequestSeq) return;
        self.feedError = e instanceof Error ? e.message : "Failed to load notifications";
      } finally {
        // A superseded read must not clear a flag the newer one raised.
        if (seq === self.feedRequestSeq) self.isLoadingFeed = false;
      }
    }),
    /**
     * Quiet re-read of exactly what is on screen — for realtime events and tab
     * return.
     *
     * Deliberately not `loadFeed`: that raises `isLoadingFeed`, which swaps the
     * rendered list for the loading state, and refetches only the first page,
     * so every page the user pulled in with "show more" would vanish because a
     * teammate touched an issue. Re-reads `feed.length` rows from offset 0
     * instead, and leaves the loading flag alone.
     */
    refreshFeed: flow(function* refreshFeed() {
      if (
        !self.lastFeedQuery ||
        !self.lastFeedBuildingId ||
        self.isLoadingFeed ||
        self.isLoadingMore
      )
        return;
      const buildingId = self.lastFeedBuildingId;
      const seq = ++self.feedRequestSeq;
      const limit = Math.max(self.feed.length, FEED_PAGE_SIZE);
      const opts = feedRepoOpts(self.lastFeedQuery);
      try {
        const res: { data: PMIssueActivity[]; count: number } =
          yield pmRepo.getActivityFeedForBuilding(buildingId, {
            ...opts,
            offset: 0,
            limit,
          });
        if (seq !== self.feedRequestSeq) return;
        self.feed = res.data;
        self.feedTotal = res.count;
        self.feedError = null;
        self.lastFeedPage = { buildingId, opts };
      } catch (e) {
        // Quiet by design: the rows on screen are still valid, so a transient
        // blip must not replace a working feed with an error banner. Logged so
        // a persistent failure is visible rather than merely stale.
        console.error("[pm] Notifications feed refresh failed", e);
      }
    }),
    // Append the next page using the query remembered from the last loadFeed.
    loadMoreFeed: flow(function* loadMoreFeed() {
      if (!self.lastFeedPage || self.isLoadingMore || self.isLoadingFeed) return;
      const { buildingId, opts } = self.lastFeedPage;
      // Shares the token with the whole-feed reads: appending a page must
      // supersede an in-flight refresh, whose shorter result would otherwise
      // land on top and drop the page just added.
      const seq = ++self.feedRequestSeq;
      self.isLoadingMore = true;
      try {
        const res: { data: PMIssueActivity[]; count: number } =
          yield pmRepo.getActivityFeedForBuilding(buildingId, {
            ...opts,
            offset: self.feed.length,
            limit: FEED_PAGE_SIZE,
          });
        if (seq !== self.feedRequestSeq) return;
        self.feed = [...self.feed, ...res.data];
        self.feedTotal = res.count;
      } catch (e) {
        if (seq !== self.feedRequestSeq) return;
        self.feedError = e instanceof Error ? e.message : "Failed to load notifications";
      } finally {
        if (seq === self.feedRequestSeq) self.isLoadingMore = false;
      }
    }),
    clearFeed() {
      // Bumping the token drops anything still in flight onto the floor, so a
      // read started for the old building cannot repopulate the cleared feed.
      self.feedRequestSeq += 1;
      self.feed = [];
      self.feedTotal = 0;
      self.feedError = null;
      self.isLoadingFeed = false;
      self.isLoadingMore = false;
      self.lastFeedBuildingId = 0;
      self.lastFeedQuery = null;
      self.lastFeedPage = null;
    },
  }));

export type NotificationsStoreInstance = Instance<typeof NotificationsStore>;
